import omnitureHelper from 'utils/omnitureHelper';
import get from 'lodash/get';
import { isEmpty } from 'lodash';
import { getDefaultCartData } from 'utils/cartUtils';
import { getCoinApplicableProducts } from 'utils/checkoutUtils';
import { ADDRESS_STATUS } from 'constants/CheckoutConstants';
import { additionalChildItemTracking } from 'pages/CartPage/CartPageAnalytics';

const pageType = 'Checkout';
const orderSummaryPageName = 'Checkout:Summary';
const loginPageName = 'Checkout:Email';
const addressPageName = 'Checkout:Address';
const paymentPageName = 'Checkout:Payment';
const abNewBucket = 'atln_co_v1';

let noPageViewFired = true;

const urgencyEvents = {
	no_sla_urgency: 'event294',
	sla_urgency_msg: 'event294,event296',
	sla_urgency_ticker: 'event294,event295'
};

function getItemTrackString(
	item,
	findingMethod = null,
	granularFindingMethod = null,
	isChildItem = false,
	forAddressView = false
) {
	if (!item) return '';

	const { category, productId, vertical, preBookInfo, trackingInfo, obdRecommendation } = item;

	const minQuantity = get(item, ['quantityMetaData', 'minQuantity']);

	let productString = `${category};${productId};;;;eVar22=${category}|eVar19=${vertical}`;

	if (findingMethod && granularFindingMethod) {
		productString += `|eVar61=${findingMethod}|eVar51=${granularFindingMethod}`;
	}

	if (preBookInfo) {
		productString += `|eVar129=Pre-Order`;
	}

	if (minQuantity && minQuantity > 1) {
		productString += `|eVar107=${minQuantity}`;
	}

	if (!forAddressView) {
		const { selected: obdFraudShown } = obdRecommendation || {};
		if (obdFraudShown) {
			productString += '|eVar158=G2N_OBD_Fraud_Shown';
		}

		const { cancellationTracking } = trackingInfo || {};
		if (cancellationTracking) {
			productString += `|eVar106=${cancellationTracking}`;
		}
	}

	if (isChildItem) {
		productString += additionalChildItemTracking(item, isChildItem);
	}

	const childItems = item.childItems;

	if (childItems && childItems.length) {
		const childString = childItems.map(item => getItemTrackString(item, null, null, true, forAddressView)).join(',');
		productString = `${productString},${childString}`;
	}
	return productString;
}

function getSLAImpact(items) {
	if (!items || items.length <= 0) {
		return '';
	}
	const itemPromiseInfos = items.map(item => get(item, ['itemPromiseInfo', 'selectedPromiseSpeedType']));
	const regularItems = itemPromiseInfos.filter(promiseType => promiseType === 'REGULAR').length;
	const premiumItems = itemPromiseInfos.filter(promiseType => promiseType === 'PREMIUM').length;
	const expressItems = itemPromiseInfos.filter(promiseType => promiseType === 'EXPRESS').length;
	return `CO_${regularItems}REGULAR_${premiumItems}PREMIUM_${expressItems}EXPRESS`;
}

// FIXME: Adding this hack to make it easy to figure out if a tracking call is
// made from an address within /checkout/retry or /checkout/init. This can be
// simplified if we are using context since react-router already does this for
// us.
const isRetry = location => {
	const pathname = get(location, ['pathname']);

	return pathname === '/checkout/retry';
};

const pageName = (originalPageName, location) => {
	if (isRetry(location)) {
		return `${originalPageName}_Retry`;
	}

	return originalPageName;
};

export function trackAddressView(checkoutData, buyNow, userState, location, addressData) {
	const omniFields = {};
	const omnievents = ['event1', 'event38'];
	const defaultCartData = getDefaultCartData(checkoutData);
	const omniContext = {
		pageName: pageName(addressPageName, location),
		pageType
	};
	const buyableStateItems = get(defaultCartData, ['buyableStateItems']) || [];
	const coldStateItems = get(defaultCartData, ['coldStateItems']) || [];
	if (!isEmpty(checkoutData)) {
		if (buyNow.eids) {
			omniFields.products = get(buyNow, ['analytics', 'products']);
		} else {
			omniFields.products = [...buyableStateItems, ...coldStateItems].reduce((acc, item) => {
				let itemTrackString = acc ? ',' : '';
				itemTrackString = `${itemTrackString}${getItemTrackString(item, null, null, false, true)}`;
				return `${acc}${itemTrackString}`;
			}, '');
		}
	}

	// Digital Freebie
	const freebieItemCount = get(checkoutData, 'checkoutSummary.freebieItemCount');
	if (freebieItemCount) {
		omnievents.push(`event684=1`);
	}

	omniFields.eVar70 = abNewBucket;

	if (noPageViewFired) {
		omnievents.push('scCheckout');
		if (buyNow && buyNow.eids) {
			omnievents.push(...['event10', 'event86']);
		} else {
			omnievents.push('event30');
		}
	}

	if (!isEmpty(addressData)) {
		const [selectedAddress] = (addressData.billingAddressInfos || []).filter(
			address => address.id === addressData.selectedAddressId
		);
		const pincode = get(selectedAddress, ['pincode']);
		pincode && (omniFields.eVar52 = pincode);
		const addressLen = get(addressData, ['billingAddressInfos', 'length']) || 0;
		omnievents.push(`event129=${addressLen}`);
		if (get(selectedAddress, 'addressValidation.failureReason')) {
			const addressStatus = get(selectedAddress, 'addressValidation.status', '');
			const addressReasonType = get(selectedAddress, 'addressValidation.failureReason.type', '');
			omniFields.prop74 = `Checkoutaddress_AIS_${addressReasonType}_${addressStatus}`;
		}
	}

	if (buyNow.eids) {
		omniFields.eVar61 = omnitureHelper.getFindingMethod(location);
		omniFields.eVar51 = omnitureHelper.getGranularFindingMethod(location);
	}

	const fields = Object.assign(
		{},
		omnitureHelper.getBaseOmnitureFields({
			...omniContext,
			location: location,
			userState: userState
		}),
		omniFields,
		{ events: omnievents.join(',') }
	);
	omnitureHelper.trackOmniturePageView(fields);
	noPageViewFired = false;
}

export function trackPaymentView(checkoutData, userState, location, addressData) {
	const omniFields = {};
	const defaultCartData = getDefaultCartData(checkoutData);
	const omnievents = ['event1', 'event38'];
	const omniContext = {
		pageName: pageName(paymentPageName, location),
		pageType
	};

	const buyableStateItems = get(defaultCartData, ['buyableStateItems']) || [];
	const serviceableItems = buyableStateItems.filter(item => item.itemState !== 'NON_SERVICEABLE');
	const nonServiceableItems = buyableStateItems.filter(item => item.itemState === 'NON_SERVICEABLE');

	omnievents.push(`event65=${nonServiceableItems.length}`);
	omnievents.push(`event125=${serviceableItems.length}`);
	if (!isEmpty(checkoutData)) {
		omniFields.products = [...serviceableItems].reduce((acc, item) => {
			let itemTrackString = acc ? ',' : '';
			itemTrackString += getItemTrackString(item);
			return acc + itemTrackString;
		}, '');
	}

	omniFields.eVar70 = abNewBucket;

	if (!isEmpty(addressData)) {
		const [selectedAddress] = (addressData.billingAddressInfos || []).filter(
			address => address.id === addressData.selectedAddressId
		);
		const pincode = get(selectedAddress, ['pincode']);
		pincode && (omniFields.eVar52 = pincode);
		const addressLen = get(addressData, ['billingAddressInfos', 'length']) || 0;
		omnievents.push(`event129=${addressLen}`);
	}

	const fields = Object.assign(
		{},
		omnitureHelper.getBaseOmnitureFields({
			...omniContext,
			location: location,
			userState: userState
		}),
		omniFields,
		{ events: omnievents.join(',') }
	);
	omnitureHelper.trackOmniturePageView(fields);
}

const getProductStringForObd = checkoutSummary => {
	const checkoutData = checkoutSummary.orderSummary;
	const defaultCartData = getDefaultCartData(checkoutData);
	const buyableStateItems = get(defaultCartData, ['buyableStateItems']) || [];
	return buyableStateItems.reduce((acc, item) => {
		if (!get(item, ['obdRecommendation', 'selected'])) {
			return acc;
		}
		let itemTrackString = acc ? ',' : '';
		itemTrackString = `${itemTrackString}${getItemTrackString(item)}`;
		return `${acc}${itemTrackString}`;
	}, '');
};

export function trackOrderSummaryView(checkoutSummary, buyNow, userState, location, addressData) {
	const omniFields = {};
	const omnievents = ['event1', 'event38'];
	const omniContext = {};

	const checkoutData = checkoutSummary.orderSummary;
	const availableCoins = get(checkoutSummary, ['accountInfo', 'availableCoins'], 0);
	const coinInfo = get(checkoutData, 'checkoutSummary.coinInfo');
	const coinApplicable = get(coinInfo, 'coinApplicable', false);
	const defaultCartData = getDefaultCartData(checkoutData);
	omniContext.pageName = pageName(orderSummaryPageName, location);
	omniContext.pageType = pageType;
	const buyableStateItems = get(defaultCartData, ['buyableStateItems']) || [];
	const serviceableItems = buyableStateItems.filter(item => item.itemState !== 'NON_SERVICEABLE');
	const nonServiceableItems = buyableStateItems.filter(item => item.itemState === 'NON_SERVICEABLE');
	const itemCountHavingCoinPrice = get(checkoutData, 'itemCountHavingCoinPrice', 0);

	omnievents.push(`event65=${nonServiceableItems.length}`);
	omnievents.push(`event125=${serviceableItems.length}`);
	omnievents.push(`event389=${itemCountHavingCoinPrice}`);
	omnievents.push(`event390=${availableCoins}`);
	omnievents.push(`event390=${availableCoins}`);

	const is2FAEnabled = get(userState, 'twoFa', false);
	// Event 477 for Verified and event 478 for Unverified visits, to be passed on Order Summary page load.
	if (is2FAEnabled) {
		omnievents.push(`event477`);
	} else {
		omnievents.push(`event478`);
	}

	if (coinApplicable) {
		omnievents.push(`event391=${coinInfo.coins}`);
	}

	if (!isEmpty(checkoutData)) {
		omniFields.products = [...serviceableItems].reduce((acc, item) => {
			let itemTrackString = acc ? ',' : '';
			if (buyNow.eids) {
				const findingMethod = omnitureHelper.getFindingMethod(location);
				const granularFindingMethod = omnitureHelper.getGranularFindingMethod(location);
				itemTrackString += getItemTrackString(item, findingMethod, granularFindingMethod);
			} else {
				itemTrackString += getItemTrackString(item);
			}
			return acc + itemTrackString;
		}, '');
	}

	const addressLen = get(addressData, ['billingAddressInfos', 'length']) || 0;
	omnievents.push(`event129=${addressLen}`);

	if (noPageViewFired) {
		omnievents.push('scCheckout');
		if (buyNow && buyNow.eids) {
			omnievents.push(...['event10', 'event86']);
		} else {
			omnievents.push('event30');
		}
	}
	omniFields.eVar46 = getSLAImpact(buyableStateItems);
	const selectedAddress =
		addressData &&
		addressData.billingAddressInfos &&
		addressData.billingAddressInfos.filter(address => address.id === addressData.selectedAddressId);
	const selectedAddressStatus = get(selectedAddress, [0, 'addressValidation', 'status']);
	const failureReasonType = get(selectedAddress, [0, 'addressValidation', 'failureReason', 'type']);
	if (selectedAddressStatus === ADDRESS_STATUS.OK) {
		omnievents.push('event208=address_selection_done');
	} else {
		omniFields.prop74 = `Checkoutaddress_AIS_${failureReasonType}_${selectedAddressStatus}`;
		omnievents.push('event209=address_selected_with_error');
	}
	const pincode = get(selectedAddress, [0, 'pincode']);
	pincode && (omniFields.eVar52 = pincode);
	omniFields.prop60 = pincode ? 'pincode_present' : 'pincode_absent';

	if (coinInfo) {
		omniFields.eVar87 = coinApplicable ? 'Enable' : 'Disable';
	}

	buyableStateItems.map(buyable => {
		buyable && buyable.nbfcOffers && (omniFields.eVar29 = 'BAJAJ_not_applicable_call');
		const slaTrackingEvent = get(buyable, ['trackingInfo', 'slaUrgency']);
		// Adding events for 11x11 & SLA callouts
		if (slaTrackingEvent) {
			omnievents.push(urgencyEvents[slaTrackingEvent]);
		}
	});

	omniFields.eVar70 = abNewBucket;

	const availablePromise =
		get(checkoutSummary.orderSummary, [
			'requestedStores',
			0,
			'storeSummary',
			'storePromiseInfo',
			'availablePromises',
			0
		]) || {};
	const deliverySlotsInfo = get(availablePromise, ['deliverySlotsInfo']) || {};
	const deliverySlotsOrder = get(availablePromise, ['deliverySlotsOrder']) || [];
	const { list1, list2 } = getDeliverySlotsAvailability(deliverySlotsInfo, deliverySlotsOrder);
	omniFields.eVar124 = list1.join(',');
	omniFields.eVar125 = list2.join(',');

	const fields = Object.assign(
		{},
		omnitureHelper.getBaseOmnitureFields({
			...omniContext,
			location: location,
			userState: userState
		}),
		omniFields,
		{ events: omnievents.join(',') }
	);
	omnitureHelper.trackOmniturePageView(fields);
	noPageViewFired = false;
}

export function trackLoginView(userState, buyNow, checkoutData, location) {
	const omniFields = {};
	const omnievents = ['event1', 'event12'];
	const omniContext = {};
	omniContext.pageName = loginPageName;
	omniContext.pageType = pageType;
	if (noPageViewFired) {
		omnievents.push('scCheckout');
		if (buyNow.eids) {
			omnievents.push(...['event10', 'event86']);
		} else {
			omnievents.push('event30');
		}
	}
	omniFields.eVar70 = abNewBucket;
	omniFields.prop21 = 'Login2Step:Displayed';

	if (buyNow.eids) {
		omniFields.products = get(buyNow, ['analytics', 'products']);
	}

	const fields = Object.assign(
		{},
		omnitureHelper.getBaseOmnitureFields({
			pageName: omniContext.pageName,
			pageType: omniContext.pageType,
			location: location,
			userState: userState
		}),
		omniFields,
		{ events: omnievents.join(',') }
	);
	omnitureHelper.trackOmniturePageView(fields);
	noPageViewFired = false;
}

export function trackMoveToCartItem(item) {
	const pageName = 'Checkout:Summary';
	const omnitureFields = {
		events: 'et',
		products: getItemTrackString(item),
		pagename: pageName,
		prop3: pageType,
		prop4: pageName
	};
	omnitureHelper.trackLink(omnitureFields, 'Summary Removal');
}

export function trackQuantityChange(item) {
	const pageName = 'Checkout:Summary';
	omnitureHelper.trackLink(
		{
			events: 'event99',
			products: getItemTrackString(item),
			pagename: pageName,
			prop3: pageType,
			prop4: pageName
		},
		'SummaryQuantityChange'
	);
}

export function trackDeliveryPromiseChange(item, serviceValue) {
	const pageName = 'Checkout:Summary';
	omnitureHelper.trackLink(
		{
			events: `event123=CO_${serviceValue}`,
			products: getItemTrackString(item),
			pagename: pageName,
			prop3: pageType,
			prop4: pageName
		},
		'Summary:SLAChange'
	);
}

export function trackASMCheckoutExpiry(item) {
	const pageName = 'Checkout:Summary';
	const omnitureFields = {
		events: 'event176',
		products: getItemTrackString(item),
		prop3: pageType,
		prop4: pageName
	};

	if (item.reservationDetails) {
		omnitureFields.products += `|evar94=${item.reservationDetails.saleId}_${item.reservationDetails.asmDisplayState}`;
	}

	omnitureHelper.trackLink(omnitureFields, 'ASM timed out');
}

export function trackOBDKnowMoreLinkClick(item) {
	const omnitureFields = {
		prop3: pageType,
		products: getItemTrackString(item),
		eVar50: orderSummaryPageName
	};

	omnitureHelper.trackLink(omnitureFields, 'G2N_KnowMore_OBD_Click');
}

export function trackCancellationLinkClick(item) {
	omnitureHelper.trackLink(
		{
			prop3: pageType,
			products: getItemTrackString(item),
			eVar50: orderSummaryPageName,
			events: 'event560'
		},
		'G2N_Cancellation_fee_callout_Click'
	);
}

export function trackForcedNonForcedOBD(checkoutSummary, continueClick) {
	const value = continueClick ? 'G2N_OBD_Forced_Pop_up_Accept and continue' : 'G2N_OBD_Forced_Pop_up_Shown';
	const omnitureFields = {
		prop3: pageType,
		products: getProductStringForObd(checkoutSummary),
		eVar50: orderSummaryPageName
	};
	omnitureHelper.trackLink(omnitureFields, value);
}

export function resetCheckoutAnalytics() {
	noPageViewFired = true;
}

export function trackManageCoinClick() {
	const omnitureFields = {
		prop3: pageType,
		pagename: pageName,
		prop4: orderSummaryPageName,
		prop75: 1
	};

	omnitureHelper.trackLink(omnitureFields, 'Coin manage click');
}

export function trackCoinSelectionUpdate(isSelectionChanged, selectionErrorOccured) {
	const omnitureFields = {
		prop3: pageType,
		pagename: pageName,
		prop4: orderSummaryPageName
	};

	if (isSelectionChanged) {
		omnitureFields.prop75 = 2;
	}

	omnitureFields.prop74 = selectionErrorOccured ? 'NOT_ENOUGH_COINS_ERROR' : 'NO_ERROR';

	omnitureHelper.trackLink(omnitureFields, 'Coin selection update');
}

export function trackDeliverySlotTabNavigation(tabIndex) {
	const pageName = 'Checkout:Summary';
	const omnitureFields = {
		prop3: pageType,
		prop4: pageName,
		eVar126: `D${tabIndex + 1}`
	};
	omnitureHelper.trackLink(omnitureFields, 'AMACTION: Slot Navigation');
}

/**
 *
 * @param {*} slotTiming object of type `SlotTimings`
 * @param {*} selectedDayIndex index value of type `number` starting from `0`
 */
export function trackDeliverySlotSelection(slotTiming, selectedDayIndex) {
	const omniData = [];

	omniData.push(getSelectedDayOmniString(selectedDayIndex));
	omniData.push(getSlotTimingOmniString(slotTiming));
	omniData.push(getSlotPriceOmniString(slotTiming));
	omniData.push(getSlotAvailabilityOmniString(slotTiming));

	const pageName = 'Checkout:Summary';
	const omnitureFields = {
		prop3: pageType,
		prop4: pageName,
		eVar127: omniData.join('_')
	};
	omnitureHelper.trackLink(omnitureFields, 'AMACTION: Slot Selection');
}

/**
 * Returns list of Slot data strings which is divided into two lists
 *
 * Slot data string e.g - D1_S630A830P_10R_A
 *
 * @param {*} deliverySlotsInfo object of type `Record<string, DeliverySlots>`
 * @param {*} deliverySlotsOrder list of `string`s
 */
function getDeliverySlotsAvailability(deliverySlotsInfo, deliverySlotsOrder) {
	const omniData = [];
	deliverySlotsOrder.forEach((key, index) => {
		const slotTimings = get(deliverySlotsInfo, [key, 'slotTimings']);
		if (slotTimings) {
			slotTimings.forEach(slot => {
				const slotData = [];
				slotData.push(getSelectedDayOmniString(index));
				slotData.push(getSlotTimingOmniString(slot));
				slotData.push(getSlotPriceOmniString(slot));
				slotData.push(getSlotAvailabilityOmniString(slot));
				omniData.push(slotData.join('_'));
			});
		}
	});
	const mid = Math.ceil(omniData.length / 2);
	return {
		list1: omniData.slice(0, mid),
		list2: omniData.slice(mid)
	};
}

/**
 * Returns the required string for `Day`
 *
 * e.g D1
 * @param {*} index of type `number`
 */
function getSelectedDayOmniString(index) {
	return `D${index + 1}`;
}

/**
 * Returns the required string for `Slot Timing`
 *
 * e.g S630PE830P where
 *
 * S   	- start time
 *
 * 630 	- 6:30
 *
 * P	- AM/PM
 *
 * E   	- end time
 *
 * 830 	- 8:30
 *
 * P	- AM/PM
 * @param slotTiming object of type `SlotTimings`
 */
function getSlotTimingOmniString(slotTiming) {
	const formatTime = dateStr => {
		const date = new Date(dateStr);
		let hours = date.getHours();
		let minutes = date.getMinutes();
		const ampm = hours >= 12 ? 'P' : 'A';
		hours = hours % 12;
		hours = hours ? hours : 12; // the hour '0' should be '12'
		minutes = minutes < 10 ? `0${minutes.toString()}` : minutes.toString();
		const strTime = hours.toString() + minutes.toString() + ampm;
		return strTime;
	};
	return `S${formatTime(slotTiming.fromSlotId)}_E${formatTime(slotTiming.toSlotId)}`;
}

/**
 * @param {*} slotTiming object of type `SlotTimings`
 */
function getSlotAvailabilityOmniString(slotTiming) {
	return slotTiming.slotAvailable ? 'A' : 'N';
}

/**
 * Returns the string for `Slot Price`
 *
 * e.g 10R
 * @param {*} slotTiming object of type `SlotTimings`
 */
function getSlotPriceOmniString(slotTiming) {
	return `${slotTiming.totalDeliveryCharge || 0}R`;
}
